import type { Mixpanel } from 'mixpanel-browser'
import Storage, { onLogout } from 'storage/auth'
import EstablishmentStorage from 'storage/establishment'
import type { AnalyticsEventMap, AnalyticsEventName } from './events'
import { loadMixpanel } from './mixpanelClient'
import { pseudonymize } from './pseudonymize'

export type {
  AnalyticsEventMap,
  AnalyticsEventName,
  XrayListFilterName
} from './events'
export { fileSizeBucket } from './events'

let clientPromise: Promise<Mixpanel> | null = null
let client: Mixpanel | null = null
let identifiedAs: string | null = null
// Fila serial: o hash é assíncrono e não pode inverter a ordem dos eventos.
let queue: Promise<void> = Promise.resolve()

// Em produção a falha fica silenciosa; em dev ela precisa aparecer, ou um token
// ou projeto errado passa despercebido.
const warnInDev = (error: unknown) => {
  if (import.meta.env.DEV) console.warn('[analytics]', error)
}

const mixpanelToken = () => import.meta.env.VITE_MIXPANEL_TOKEN?.trim()

const getClient = (token: string) => {
  clientPromise ??= loadMixpanel(token).then(loaded => {
    client = loaded
    return loaded
  })
  return clientPromise
}

const send = async <E extends AnalyticsEventName>(
  name: E,
  props: AnalyticsEventMap[E]
) => {
  const token = mixpanelToken()
  if (!token) return

  const user = Storage.user.get()
  if (!user?.professional_id || user.super_admin) return

  const mixpanel = await getClient(token)

  const distinctId = await pseudonymize(user.professional_id)
  if (identifiedAs !== distinctId) {
    mixpanel.identify(distinctId)
    identifiedAs = distinctId
  }

  const establishmentId = EstablishmentStorage.currentEstablishment.getId()
  mixpanel.track(name, {
    ...props,
    ...(establishmentId
      ? { establishment: await pseudonymize(establishmentId) }
      : {})
  })
}

/**
 * Envia um evento do catálogo. No-op sem `VITE_MIXPANEL_TOKEN`, sem sessão ou
 * para super admin. O SDK só é carregado (e o usuário identificado) no primeiro
 * evento, então quem não usa o módulo rastreado não envia nada. Falhas são
 * ignoradas: tracking nunca quebra a tela.
 */
export const track = <E extends AnalyticsEventName>(
  name: E,
  props: AnalyticsEventMap[E]
): void => {
  queue = queue.then(() => send(name, props)).catch(warnInDev)
}

/** Limpa a identidade no Mixpanel. Roda sozinho no `Auth.logout`. */
export const reset = (): void => {
  identifiedAs = null
  try {
    client?.reset()
  } catch (error) {
    warnInDev(error)
  }
}

onLogout(reset)

/** Aguarda os eventos pendentes; uso em testes. */
export const flush = (): Promise<void> => queue

const analytics = { track, reset }

export default analytics
